import { useEffect, useRef, useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';

export type LottieType = {
  contentful_id: string;
  title: string;
  file: {
    contentType: `application/json`;
    url: string;
  };
  fields?: {
    fileContent: string;
  };
};

export type LottiePlayerProps = Omit<React.ComponentProps<typeof Player>, 'src' | 'renderer'>;

export type LottieProps = LottiePlayerProps & {
  lottie?: LottieType;
  src?: React.ComponentProps<typeof Player>['src'];
  isStopped?: boolean;
  isPaused?: boolean;
};

export const Lottie: React.FC<LottieProps> = ({ lottie, src, isStopped, isPaused, ...props }) => {
  const [display, setDisplay] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const lottieSrc = src || lottie?.fields?.fileContent;

  useEffect(() => {
    let observer: IntersectionObserver;

    if (ref.current) {
      const handleIntersection: IntersectionObserverCallback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setDisplay(true);
            observer.unobserve(entry.target);
          }
        });
      };

      observer = new IntersectionObserver(handleIntersection, { rootMargin: '200px' });
      observer.observe(ref.current);
    }

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    if (isStopped) playerRef.current?.stop();
    else if (isPaused) playerRef.current?.pause();
    else if (!isStopped && !isPaused) playerRef.current?.play();
  }, [isStopped, isPaused, playerRef.current]);

  return lottieSrc ? (
    <div ref={ref}>{display && <Player ref={playerRef} src={lottieSrc} renderer='svg' {...props} autoplay loop />}</div>
  ) : null;
};
